import {ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {Router, ActivatedRoute} from "@angular/router";
import {StartupService} from "../../service-ui/startup.service";
import {MatSidenav} from "@angular/material/sidenav";
import {MatMenuTrigger} from "@angular/material/menu";
import {MediaMatcher} from "@angular/cdk/layout";
import {Draft} from "../../../model/Draft";
import {DraftService} from "../../service-data/draft.service";
import {ResourceService} from "../../service-data/resource.service";
import {Subject} from "rxjs";
import {DataObject} from "../../../model/DataObject";
import {takeUntil} from "rxjs/operators";
import {ProjectService} from "../../service-data/project.service";
import {Project} from "../../../model/Project";
import {Util} from "../../util/Util";
import {Organization} from "../../../model/Organization";
import {ProjectFolders} from "../../../model/supporting/ProjectFolders";
import {MatDialog} from "@angular/material/dialog";
import {RenameProjectFolderComponent} from "./project/dialogs/rename-project-folder/rename-project-folder.component";
import {OrganizationService} from "../../service-data/organization.service";
import {BookService} from "../../service-data/book.service";
import {Book} from "../../../model/Book";
import {MoveProjectFolderComponent} from "./project/dialogs/move-project-folder/move-project-folder.component";
import {CreateProjectFolderComponent} from "./project/dialogs/create-project-folder/create-project-folder.component";
import {MoveProjectComponent} from "./project/dialogs/move-project/move-project.component";
import {DeleteProjectFolderComponent} from "./project/dialogs/delete-project-folder/delete-project-folder.component";
import {ProjectDraftComponent} from "./draft/project-draft.component";
import {OrganizeProjectsComponent} from "./project/dialogs/organize-projects/organize-projects.component";
import {countOrphanBooks, countOrphanDrafts, isArchivedBook, isProofBook} from "../../service-ui/orphan-items";
import {ServerErrorToastService} from "../../service-ui/server-error-toast.service";
import {CreateProjectLauncherService} from "../../service-ui/create-project-launcher.service";
import {modernDialogConfig} from "../../service-ui/dialog-config";
import {LanguagePipe} from "../../pipe/language.pipe";


@Component({
  selector: 'app-projects',
  templateUrl: './projects.component.html',
  styleUrls: ['./projects.component.css']
})

export class ProjectsComponent implements OnInit, OnDestroy, LoggedInCallback {
  @ViewChild('mainSideNav', { static: true }) mainSideNav: MatSidenav;
  @ViewChild('draftComponent') draftComponent: ProjectDraftComponent;
  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;
  onDestroySubject: Subject<boolean> = new Subject();
  // Stored ChangeDetectorRef so we can trigger a recompute when dialogs close
  private changeDetectorRefStored: ChangeDetectorRef;

  protected readonly Util = Util;
  protected readonly Boolean = Boolean;
  protected readonly String = String;

  goose: number = 1;
  items: Project[] = [];
  selectedItem: Project;

  /**
   * The specific Draft / Book the operator opened, when they opened a tile
   * rather than the Project as a whole.
   *
   * The detail pane used to render `selectedItem.primaryDraft` /
   * `.primaryBook` unconditionally, so "Edit Draft" or "Open Book" on any
   * non-primary tile quietly opened the primary one instead. These are set
   * from the thumbnails' `selectedItem` output and cleared whenever the
   * Project selection changes, so the primary stays the fallback when nothing
   * more specific was chosen.
   */
  selectedDraft: Draft;
  selectedBook: Book;

  searchString: string = '';

  role: string;
  showLanguagesMenu = false;
  // showProjects: boolean = true;
  showBooks: boolean = true;
  showDrafts: boolean =  true;
  expand: boolean = false;
  showGrid: boolean = true;
  showSplitView: boolean = false;
  editingDetails: string = '';
  displayCover: boolean = true;
  displaySettings: boolean = true;
  displayMode: string = 'PROJECT'; // 'PROJECT', 'DRAFT', or 'BOOK'
  showOnlyPrimary: boolean = true;

  resourceURLS = {};
  draftURLS = {};

  selectedFolder = '/';
  folderDecendantProjectCount = {};

  organization: Organization;
  projectFolders: ProjectFolders;

  // ─────────────────────────────────────────────────────────
  // Folder problems
  //
  // A Project carries its folder as a STRING PATH; the list of
  // folders that exist is a separate JSON tree on the
  // Organization. Nothing keeps the two in step (see the
  // `healOrganizationFolderStructure` comment below for the
  // four known ways they drift), so a Project can sit in a
  // folder the tree has never heard of.
  //
  // That used to be invisible AND disabling: the grid shows a
  // Project only when the selected folder equals its folder,
  // and the only way to select a folder is to click it in the
  // tree — so a Project in a folder missing from the tree could
  // not be reached from this screen at all. It still existed,
  // still shipped, still appeared in orders; it just could not
  // be opened.
  //
  // Now it is reported instead. `folderProblems` drives a
  // banner naming each missing folder path, and the Projects in
  // those folders are listed in the base folder so they stay
  // reachable while the tree is wrong. Repair is a button the
  // operator presses, not something that happens silently on
  // load.
  // ─────────────────────────────────────────────────────────

  /** One entry per distinct folder path that no longer exists in the tree. */
  folderProblems: { folder: string, projects: Project[] }[] = [];

  /** Every Project whose folder is missing from the tree, flattened. */
  projectsInMissingFolders: Project[] = [];

  /** Last problem signature logged, so the console gets one line per change. */
  private lastFolderProblemSignature = '';

  get hasFolderProblems(): boolean {
    return this.folderProblems.length > 0;
  }

  get folderProblemProjectCount(): number {
    return this.projectsInMissingFolders.length;
  }

  /** True when the grid is showing the tree root, however it is spelled. */
  get isRootFolderSelected(): boolean {
    return Util.normalizeFolderPath(this.selectedFolder) === '';
  }

  /**
   * Does this Project live in this folder?
   *
   * Compares NORMALIZED paths. The root is spelled `''` in `Project.folder`
   * (the model default, and what the reset script writes) and `'/'` in
   * `selectedFolder` (the initial value, and what the breadcrumb sets), and a
   * raw `===` between those two says a Project at the root is not at the root.
   * Trailing slashes have the same effect one level down.
   */
  isProjectInFolder(project: Project, folder: string): boolean {
    if ( !project ) { return false; }
    return Util.normalizeFolderPath(project.folder) === Util.normalizeFolderPath(folder);
  }

  /** Is this Project's folder absent from the Organization's tree? */
  isProjectFolderMissing(project: Project): boolean {
    if ( !project || !this.projectFolders ) { return false; }
    const folder = Util.normalizeFolderPath(project.folder);
    if ( folder === '' ) { return false; }   // the root always exists
    try {
      return this.projectFolders.getFlattened().indexOf(folder) === -1;
    } catch ( e ) {
      return false;
    }
  }

  /**
   * Recompute {@link folderProblems} from the loaded Projects and the stored
   * tree. Pure — reads both, writes neither. Cheap enough to run on every
   * notification: one pass over the Projects against a Set of tree paths.
   */
  detectFolderProblems(): void {
    this.folderProblems = [];
    this.projectsInMissingFolders = [];
    if ( !this.projectFolders || !this.items ) {
      return;
    }

    let existing: Set<string>;
    try {
      existing = new Set<string>(this.projectFolders.getFlattened() || []);
    } catch ( e ) {
      console.error('detectFolderProblems: could not enumerate the folder tree', e);
      return;
    }

    const byFolder: { [folder: string]: Project[] } = {};
    for ( const project of this.items ) {
      if ( !project ) { continue; }
      const folder = Util.normalizeFolderPath(project.folder);
      if ( folder === '' || existing.has(folder) ) { continue; }
      if ( !byFolder[folder] ) { byFolder[folder] = []; }
      byFolder[folder].push(project);
      this.projectsInMissingFolders.push(project);
    }

    this.folderProblems = Object.keys(byFolder).sort().map(folder => {
      return { folder: folder, projects: byFolder[folder] };
    });

    // One console line per CHANGE, not per recompute — this runs on every
    // Project notification and a line each time would bury it. The signature
    // is what makes "has anything changed?" cheap.
    const signature = this.folderProblems.map(p => `${p.folder}:${p.projects.length}`).join('|');
    if ( signature !== this.lastFolderProblemSignature ) {
      this.lastFolderProblemSignature = signature;
      if ( signature ) {
        console.warn(`Folder tree DIVERGED for Organization ${this.organization ? this.organization.ID : '?'}: `
          + `${this.folderProblems.length} folder(s) referenced by a Project but absent from the stored tree `
          + `[${signature}]. Reported in the UI; not repaired automatically. If this recurs, the generic `
          + `PUT /project and PUT /organization endpoints are the suspected cause.`);
      }
    }
  }

  // ─────────────────────────────────────────────────────────
  // Orphan-detection getters
  //
  // Both counts come from `service-ui/orphan-items.ts`, which is the
  // single definition of "not in a Project" shared with
  // `OrganizeProjectsComponent`. That sharing is the point: the banner
  // exists only to send the operator to Organize Projects, so if the two
  // screens can disagree about what an orphan is, the banner can nag
  // about work the dialog will not show. They used to disagree -- the
  // Draft count filtered nothing at all, so a Draft left in the cache
  // from a previously-viewed Organization was counted as outstanding
  // work forever.
  //
  // Recomputed on every access -- the underlying arrays live on
  // the service, and Angular change detection already runs at
  // template-friendly cadence. For typical catalogue sizes the
  // cost is negligible; if it ever becomes a hotspot we can
  // memoise off the services' add/update notifiers.
  // ─────────────────────────────────────────────────────────

  get orphanBookCount(): number {
    return countOrphanBooks(
      (this.bookService && this.bookService.objects) || [],
      this.organization && this.organization.ID);
  }

  get orphanDraftCount(): number {
    return countOrphanDrafts(
      (this.draftService && this.draftService.objects) || [],
      this.organization && this.organization.ID);
  }

  get orphanCount(): number {
    return this.orphanBookCount + this.orphanDraftCount;
  }

  get hasOrphans(): boolean {
    return this.orphanCount > 0;
  }

  // ─────────────────────────────────────────────────────────
  // First-run empty state
  //
  // A brand-new Organization has no Projects, no Books and no
  // Drafts, and every screen that could help sits behind a toolbar
  // button. Nothing on the page said what to do first, and the one
  // place that tried — the empty cart — linked to `/secure/books`,
  // a route that has never existed (see `app-routing.module.ts`),
  // so its button did nothing at all.
  //
  // `hasNothingAtAll` is the true first-run case and gets the full
  // explanation. `hasNoProjectsHere` is the weaker "this folder is
  // empty" case; it still offers Create Project but does not claim
  // the account is empty, because the operator may have plenty of
  // content one folder over.
  // ─────────────────────────────────────────────────────────

  /** No Project is visible in the current folder / search. */
  get hasNoProjectsHere(): boolean {
    return !this.items || this.items.length === 0;
  }

  /** No Projects, no Books, no Drafts — the account is empty. */
  get hasNothingAtAll(): boolean {
    if ( !this.hasNoProjectsHere ) {
      return false;
    }
    const books = (this.bookService && this.bookService.objects) || [];
    const drafts = (this.draftService && this.draftService.objects) || [];
    return books.length === 0 && drafts.length === 0;
  }

  /**
   * Create Project from the empty state. Same flow as the toolbar
   * button, rooted at the folder currently in view so the new
   * Project lands where the operator is looking.
   */
  createFirstProject(): void {
    this.createProject(this.selectedFolder || '/');
  }

  /**
   * Stringly-typed `Book.archived` sentinel — treat any
   * case-insensitive `'TRUE'` as archived, everything else
   * (including NULL / '' / 'FALSE' / undefined) as active. Kept
   * as a public method so both this class's own getters AND the
   * template can call it inline without duplicating the
   * comparison. Archived Books stay in the cache to resolve
   * historical Order / Package references but should not appear
   * in any active-catalogue surface — this predicate is the
   * single point that decides "should this Book be surfaced to
   * the operator here?".
   */
  isArchived(book: any): boolean {
    return isArchivedBook(book);
  }

  /**
   * Stringly-typed `Book.isProof` sentinel — proofs are one-off
   * physical samples generated to review an item, not catalogue
   * titles. They cluster with archived Books in intent (present
   * in the cache for order-history resolution, but not
   * meaningful to the active-catalogue Projects UI). Handled as
   * a peer of `isArchived()` rather than folded into it so the
   * two states remain independently observable (a Book can be
   * a proof without being archived and vice versa).
   */
  isProof(book: any): boolean {
    return isProofBook(book);
  }


  // ─────────────────────────────────────────────────────────
  // MULTI-SELECT
  //
  // Google Drive conventions, because that is what the operator
  // already knows: a single click SELECTS, Ctrl+Click adds or
  // removes one, Shift+Click takes a range, and OPENING moved to
  // double click. That last part is not a preference — a single
  // click that navigates away can never be the first half of a
  // range, so selection and single-click-to-open cannot both
  // exist on the same tile.
  //
  // Selection is held as an array of IDS, not of Project objects.
  // 'addUpdateProject' pushes whatever instance the notifier hands
  // it, so the object sitting in 'items' can be replaced between
  // the click that selected it and the action that consumes the
  // selection. An identity-keyed Set would then be holding a
  // Project that is no longer on screen, and the Move dialog would
  // write to it.
  //
  // The bar at the top of the grid and the right-click menu are
  // two presentations of ONE selection and ONE action list. Move
  // is the only action today; anything added later belongs in both
  // places, which is why they share 'selectionCount' and
  // 'moveSelectedProjects()' rather than each growing its own.
  // ─────────────────────────────────────────────────────────

  /** Ids of the Projects selected in the grid. See the block comment above. */
  selectedProjectIds: number[] = [];

  /**
   * Where a Shift+Click range starts.
   *
   * Set by plain clicks and Ctrl+Clicks, deliberately NOT by a
   * Shift+Click: successive Shift+Clicks must re-range from the
   * same origin (grow and shrink one selection) rather than walk
   * the anchor along behind the pointer, which would make the
   * range impossible to shrink.
   */
  private selectionAnchorId = 0;

  /** Viewport coordinates the right-click menu opens at. */
  contextMenuPosition = { x: '0px', y: '0px' };

  @ViewChild('selectionMenuTrigger') selectionMenuTrigger: MatMenuTrigger;

  /**
   * The selected Projects that are still on screen, in grid order.
   *
   * Derived rather than stored, so a selection cannot outlive what
   * it points at: changing folder, typing in the search box or
   * switching display mode all narrow the grid, and a count of
   * "5 selected" against three visible tiles is how the wrong
   * Projects get moved. Ordering matches the grid because Move
   * reports progress in that order.
   */
  get selectedProjectList(): Project[] {
    if ( !this.selectedProjectIds.length ) {
      return [];
    }
    return this.visibleProjects.filter(p => this.isSelected(p));
  }

  get selectionCount(): number {
    return this.selectedProjectList.length;
  }

  get hasSelection(): boolean {
    return this.selectionCount > 0;
  }

  isSelected(project: Project): boolean {
    if ( !project ) { return false; }
    return this.selectedProjectIds.indexOf(Number(project.getId())) !== -1;
  }

  clearSelection(): void {
    this.selectedProjectIds = [];
    this.selectionAnchorId = 0;
  }

  /**
   * Would this Project draw a tile right now?
   *
   * One predicate, three consumers: the grid renders from it, a
   * Shift+Click range walks it, and 'selectedProjectList' prunes
   * against it. They have to agree — a range computed over a
   * different set than the one drawn selects tiles the operator
   * cannot see, and the selection bar then offers to move them.
   */
  isProjectVisible(project: Project): boolean {
    if ( !project ) { return false; }
    const search = (this.searchString || '').toLowerCase();
    if ( search ) {
      if ( !project.name || project.name.toLowerCase().indexOf(search) === -1 ) {
        return false;
      }
    } else if ( !this.isProjectInFolder(project, this.selectedFolder) ) {
      return false;
    }
    return this.hasTileInDisplayMode(project);
  }

  /**
   * DRAFT and BOOK modes draw a tile per Draft / per Book, so a
   * Project with neither contributes nothing to the grid in those
   * modes. Without this a Shift+Click range would count rows that
   * render as nothing, and the range would select fewer tiles than
   * it crossed.
   */
  hasTileInDisplayMode(project: Project): boolean {
    if ( !project ) { return false; }
    if ( this.displayMode === 'BOOK' ) {
      if ( project.primaryBook && !this.isArchived(project.primaryBook) ) { return true; }
      return !this.showOnlyPrimary && !!project.books
        && project.books.some(b => b && !this.isArchived(b));
    }
    if ( this.displayMode === 'DRAFT' ) {
      if ( project.primaryDraft ) { return true; }
      return !this.showOnlyPrimary && !!project.drafts && project.drafts.length > 0;
    }
    return true;
  }

  /**
   * The Projects the grid is drawing, in the order it draws them.
   *
   * Projects in missing folders come first because the template
   * puts that section first — a Shift+Click range that crossed
   * the two sections in a different order than the eye does would
   * select a different set than the one highlighted.
   */
  get visibleProjects(): Project[] {
    const ordered: Project[] = [];
    if ( !this.searchString && this.isRootFolderSelected ) {
      for ( const p of this.projectsInMissingFolders ) {
        if ( this.hasTileInDisplayMode(p) ) { ordered.push(p); }
      }
    }
    for ( const p of this.items || [] ) {
      if ( this.isProjectVisible(p) && ordered.indexOf(p) === -1 ) { ordered.push(p); }
    }
    return ordered;
  }

  /**
   * Single click on a tile. Selects; never opens.
   *
   * Clicks that land on a control INSIDE the tile — the overflow
   * menu, the inline title editor, a resource badge — are left
   * alone. Those act on one specific Draft or Book and have
   * nothing to do with which Projects are selected; changing the
   * selection on the way to pressing one would be surprising, and
   * for the menu it would happen behind the panel that opens.
   */
  onProjectTileClick(project: Project, event: MouseEvent): void {
    if ( !project || this.isInteractiveTarget(event) ) {
      return;
    }
    const id = Number(project.getId());

    if ( event.shiftKey && this.selectionAnchorId ) {
      this.selectRangeTo(id);
      return;
    }

    if ( event.ctrlKey || event.metaKey ) {
      if ( this.selectedProjectIds.indexOf(id) === -1 ) {
        this.selectedProjectIds = this.selectedProjectIds.concat([id]);
      } else {
        this.selectedProjectIds = this.selectedProjectIds.filter(x => x !== id);
      }
      this.selectionAnchorId = id;
      return;
    }

    this.selectedProjectIds = [id];
    this.selectionAnchorId = id;
  }

  /** Select every tile between the anchor and 'id', inclusive. */
  private selectRangeTo(id: number): void {
    const ordered = this.visibleProjects.map(p => Number(p.getId()));
    const from = ordered.indexOf(this.selectionAnchorId);
    const to = ordered.indexOf(id);
    if ( from === -1 || to === -1 ) {
      // The anchor scrolled out of the current filter. Degrade to a
      // plain click rather than selecting a range against a list the
      // anchor is not in, which would silently pick from index 0.
      this.selectedProjectIds = [id];
      this.selectionAnchorId = id;
      return;
    }
    this.selectedProjectIds = ordered.slice(Math.min(from, to), Math.max(from, to) + 1);
  }

  /**
   * Right click on a tile. Same actions as the selection bar,
   * against the same selection.
   *
   * A right click on a tile that is NOT selected selects it and
   * drops whatever was selected before — that is what every file
   * manager does, and the alternative (acting on a selection the
   * operator is not pointing at) is precisely how the wrong
   * Projects get moved. A right click INSIDE the current selection
   * leaves it alone, which is the entire point of having built one.
   */
  onProjectTileContextMenu(project: Project, event: MouseEvent): void {
    if ( !project || this.isInteractiveTarget(event) ) {
      return;
    }
    event.preventDefault();
    if ( !this.isSelected(project) ) {
      const id = Number(project.getId());
      this.selectedProjectIds = [id];
      this.selectionAnchorId = id;
    }
    this.contextMenuPosition.x = event.clientX + 'px';
    this.contextMenuPosition.y = event.clientY + 'px';

    // Flush those two bindings BEFORE opening.
    //
    // MatMenuTrigger positions the panel against its HOST element, and
    // reads that host's bounding rect synchronously inside openMenu().
    // The host only moves when the bindings above are written to the DOM,
    // which Angular does on the tick that follows this handler — so
    // without this call the overlay measures the host where the PREVIOUS
    // right-click left it, and the menu opens one click behind the
    // pointer forever. Nothing repositions it afterwards; the strategy
    // re-applies on scroll and resize, not on the origin moving.
    try { this.changeDetectorRefStored.detectChanges(); } catch ( e ) { /* view may be gone */ }

    if ( this.selectionMenuTrigger ) {
      this.selectionMenuTrigger.openMenu();
    }
  }

  /**
   * A click on the grid itself — not on any tile — clears the
   * selection, the way clicking empty space does in a file
   * manager. Folder tiles count as empty space: they are not part
   * of a Project selection and pressing one navigates anyway.
   */
  onGridBackgroundClick(event: MouseEvent): void {
    const target = event ? event.target as HTMLElement : undefined;
    if ( target && target.closest && target.closest('.project-tile') ) {
      return;
    }
    this.clearSelection();
  }

  /** Escape drops the selection, as it does everywhere else. */
  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if ( this.selectedProjectIds.length ) {
      this.clearSelection();
    }
  }

  /**
   * Did this click land on something that handles its own clicks?
   *
   * Matched off the DOM rather than by wiring 'stopPropagation()'
   * into every control inside three separate thumbnail components.
   * '.pt-res-id' is listed explicitly because it is a span acting
   * as a button (it advances a Resource stage) and matches none of
   * the element selectors.
   */
  private isInteractiveTarget(event: MouseEvent): boolean {
    const target = event ? event.target as HTMLElement : undefined;
    if ( !target || !target.closest ) { return false; }
    return !!target.closest('button, a, input, textarea, select, .pt-res-id, .mat-mdc-menu-panel');
  }

  /**
   * Open the one selected Project. Offered only for a selection of
   * one: the detail pane shows exactly one Project, so "open" has
   * no meaning for five at once.
   */
  openSelectedProject(): void {
    const selected = this.selectedProjectList;
    if ( selected.length !== 1 ) { return; }
    this.openProject(selected[0]);
  }

  /**
   * Open a Project on whichever surface it actually has: its Book,
   * else its Draft, else the Project itself. Deliberately the same
   * order the cover tile uses on double click, so the context menu
   * and the tile cannot land the operator on different screens.
   */
  openProject(project: Project): void {
    if ( !project ) { return; }
    this.selectedItemChanged(project);
    if ( project.primaryBook && !this.isArchived(project.primaryBook) ) {
      this.editingDetails = 'BOOK';
    } else if ( project.primaryDraft ) {
      this.editingDetails = 'DRAFT';
    } else {
      this.editingDetails = 'PROJECT';
    }
  }

  /**
   * Move every selected Project into one folder.
   *
   * One dialog serves one Project and many — see
   * 'MoveProjectComponent', which issues one PUT per Project
   * because 'PUT /project/{id}/folder' is per-Project and carries a
   * per-Project optimistic-concurrency check that has no batch
   * form. A partial failure is therefore possible and is reported
   * there rather than swallowed here.
   */
  moveSelectedProjects(): void {
    const selected = this.selectedProjectList;
    if ( selected.length === 0 ) {
      return;
    }
    const dialogConfig = modernDialogConfig({ width: '640px' });
    dialogConfig.data = { organization: this.organization, projects: selected };
    const dialogRef = this.dialog.open(MoveProjectComponent, dialogConfig);
    dialogRef.afterClosed().subscribe(result => {
      // Folder counts and the folder-problem banner are both derived
      // from 'project.folder', and a move is the one thing that
      // changes it. Leaving them alone would badge the folders the
      // Projects came out of.
      this.computeFolderDecendantProjectCount();
      if ( result && result.moved ) {
        this.clearSelection();
      }
      try { this.changeDetectorRefStored.detectChanges(); } catch ( e ) { /* view may be gone */ }
    });
  }

  constructor(public router: Router, public startupService: StartupService, private organizationService: OrganizationService, private projectService: ProjectService, private draftService: DraftService, private bookService: BookService, public resourceService: ResourceService, private serverErrorToastService: ServerErrorToastService, changeDetectorRef: ChangeDetectorRef, media: MediaMatcher, public dialog: MatDialog, private activatedRoute: ActivatedRoute, private createProjectLauncherService: CreateProjectLauncherService) {
    this.changeDetectorRefStored = changeDetectorRef;
    this.mobileQuery = media.matchMedia('(max-width: 600px)');
    this._mobileQueryListener = () => this.changeDetectorRefStored.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
    this.organization = startupService.getCurrentOrganization();
    this.projectFolders = this.organization.getProjectFolders();
  }

  ngOnInit() {
    console.log("AppComponent: Checking if the user is already authenticated");
    this.startupService.isAuthenticated(this);
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      console.log("Not logged in in Projects");
      this.startupService.logout();
    } else {
      this.resourceURLS = {};
      this.draftURLS = {};
      this.organization = this.startupService.getCurrentOrganization();
      this.projectFolders = this.organization.getProjectFolders();
      this.getData();
      // Must run AFTER getData(): the notifier subscriptions it sets
      // up are what let a hand-off from another screen complete once
      // the new Project / Draft actually arrive.
      this.consumeCreateProjectQueryParams();
    }
  }

  getSortedChildFolders(folder: string): string[] {
    const folders = this.projectFolders.getChildFolders(folder);
    if (!folders) {
      return [];
    }
    return [...folders].sort((a, b) => {
      const nameA = this.Util.getLastItem(a, '/').toLowerCase();
      const nameB = this.Util.getLastItem(b, '/').toLowerCase();
      if (nameA < nameB) { return -1; }
      if (nameA > nameB) { return 1; }
      return 0;
    });
  }

  /**
   * Open the "Organize Projects" dialog, which analyzes all loaded
   * Books and Drafts and suggests groupings that could become
   * Projects. The dialog does its own persistence via
   * `ProjectService` on save.
   */
  organizeProjects(): void {
    // `modernDialogConfig` rather than `new MatDialogConfig()`: a
    // constructed config sets `panelClass = ''`, which overrides the
    // app-wide `modern-dialog` default and strips the entire dialog
    // skin. See the header of `service-ui/dialog-config.ts`.
    const dialogConfig = modernDialogConfig({
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '92vh'
    }, 'wide');
    dialogConfig.data = { selectedFolder: this.selectedFolder };
    const dialogRef = this.dialog.open(OrganizeProjectsComponent, dialogConfig);
    // When the Organize Projects dialog closes, refresh project-related warnings
    // and recompute derived state so the banner / warnings reflect any changes
    // the operator may have made inside the dialog.
    dialogRef.afterClosed().subscribe(() => {
      try {
        // Re-run the lightweight data refresh that wires up notifiers
        // and recomputes folder counts. `getData(false)` avoids clearing
        // any transient UI state that might be desirable to keep.
        this.getData(false);
      } catch (e) {
        console.error('Error refreshing Projects view after Organize dialog closed', e);
      }
      // Ensure Angular picks up any changes immediately.
      try { this.changeDetectorRefStored.detectChanges(); } catch (e) { /* ignore */ }
    });
  }

  createProject (selectedFolder: string) {
    console.log(`Create Project ${selectedFolder}`);

    this.createProjectLauncherService.open(selectedFolder).subscribe(result => {
      if ( !result || !result.created ) {
        return;
      }
      // Prefer the id the server just assigned; fall back to the
      // name for older server builds that answer without `data`.
      this.pendingProjectId = result.project_ID || 0;
      this.pendingProjectName = result.projectName || '';
      this.pendingOpenDraft = !!result.openDraft;
      this.pendingSelectionDeadline = Date.now() + ProjectsComponent.PENDING_SELECTION_TIMEOUT_MS;
      // The Project usually has not arrived over NotificationsService
      // yet, so this first attempt normally misses; the notifier
      // subscriptions in `getData()` retry it as objects land.
      this.tryApplyPendingSelection();
    });
  }

  // ─────────────────────────────────────────────────────────
  // Pending selection
  //
  // "Select the Project that was just created" is not something
  // that can be done synchronously. The create call returns an id,
  // but the Project object itself reaches this screen through
  // NotificationsService some time later — and when the operator
  // created it from the CART, this component did not even exist at
  // the moment the id was known.
  //
  // Both routes therefore park the intent here and every Project /
  // Draft notification retries it. The deadline stops a Project
  // that never arrives (deleted elsewhere, notification dropped)
  // from hijacking the operator's selection minutes later, which is
  // otherwise exactly what a latched flag with no expiry does.
  // ─────────────────────────────────────────────────────────

  private static readonly PENDING_SELECTION_TIMEOUT_MS = 60000;

  private pendingProjectId = 0;
  private pendingProjectName = '';
  private pendingOpenDraft = false;
  private pendingSelectionDeadline = 0;

  /** Is there an unsatisfied "open this Project" intent still in date? */
  private get hasPendingSelection(): boolean {
    if ( !this.pendingProjectId && !this.pendingProjectName ) {
      return false;
    }
    if ( Date.now() > this.pendingSelectionDeadline ) {
      this.clearPendingSelection();
      return false;
    }
    return true;
  }

  private clearPendingSelection(): void {
    this.pendingProjectId = 0;
    this.pendingProjectName = '';
    this.pendingOpenDraft = false;
    this.pendingSelectionDeadline = 0;
  }

  /**
   * Select the pending Project once it is loaded, opening its Draft
   * when one was asked for.
   *
   * When a Draft was requested but has not linked up yet
   * (`primaryDraft` is populated by `ProjectService.getLinkData()`
   * only after the Draft object itself arrives) the intent is kept
   * so a later notification can complete it — selecting the Project
   * with `editingDetails = 'PROJECT'` and giving up would drop the
   * operator on the wrong screen for good.
   */
  private tryApplyPendingSelection(): void {
    if ( !this.hasPendingSelection ) {
      return;
    }
    const match = this.items.find(p => p
      && ( ( this.pendingProjectId && Number(p.getId()) === Number(this.pendingProjectId) )
        || ( !this.pendingProjectId && this.pendingProjectName && p.name === this.pendingProjectName ) ));
    if ( !match ) {
      return;
    }
    if ( this.pendingOpenDraft && !match.primaryDraft ) {
      // Project is here, its Draft is not yet. Wait for it.
      return;
    }

    this.selectedItem = match;
    this.editingDetails = this.pendingOpenDraft && match.primaryDraft ? 'DRAFT' : 'PROJECT';
    // A newly created Draft has no PDFs yet, so the interior is the
    // side the operator has to act on first.
    if ( this.editingDetails === 'DRAFT' ) {
      this.displayCover = false;
    }
    this.clearPendingSelection();
    try { this.mainSideNav.close(); } catch ( e ) { /* sidenav may not be ready */ }
    try { this.changeDetectorRefStored.detectChanges(); } catch ( e ) { /* view may be gone */ }
  }

  /**
   * Pick up a "create Project" hand-off from another screen.
   *
   * `CreateProjectLauncherService.createProjectAndOpenDraft` routes
   * here with `newProjectId` / `newProjectName` / `openDraft` after
   * the dialog closes elsewhere in the app (the empty cart page is
   * the first caller). The params are consumed once and then
   * stripped from the URL so a refresh — or a browser Back into
   * this route — does not re-trigger the selection.
   */
  private consumeCreateProjectQueryParams(): void {
    const params = this.activatedRoute.snapshot.queryParams || {};
    const newProjectId = Number(params['newProjectId'] || 0);
    const newProjectName = params['newProjectName'] || '';
    if ( !newProjectId && !newProjectName ) {
      return;
    }
    this.pendingProjectId = newProjectId > 0 ? newProjectId : 0;
    this.pendingProjectName = newProjectName;
    this.pendingOpenDraft = String(params['openDraft'] || '') === '1';
    this.pendingSelectionDeadline = Date.now() + ProjectsComponent.PENDING_SELECTION_TIMEOUT_MS;

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {},
      replaceUrl: true
    });

    this.tryApplyPendingSelection();
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
  }

  addUpdateProject(project: Project) {
    try {
      if ( this.matchProject(project)) {
        let found = false;
        for (const item of this.items ) {
          if ( item.getId() === project.getId() ) {
            found = true;
          }
        }
        if ( !found ) {
          project['expand'] = false;
          this.items.push(project);
        }
      }
    } catch ( e ) {
      console.log(`Error in ProjectsComponent addUpdate ${project.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
  }

  deleteProject(project: Project) {
  }

  matchProject(project: Project): boolean {
    if ( project ) {
      return true;
    }
    return false;
  }

  // Drafts and Books are held inside Projects -- so look through the projects for them when there is an update
  addUpdateDraft(draft: Draft) {
    try {
      let found = false;
      for ( const project of this.items ) {
        if ( project.drafts && !found ) {
          for ( let i = 0; i < project.drafts.length && !found; i++ ) {
            if ( project.drafts[i].getId() === draft.getId()) {
              found = true;
              project.drafts.splice(i, 1, draft);
              if ( project.primaryDraft.getId() === draft.getId()) {
                project.primaryDraft = draft;
              }
            }
          }
        }
      }
    } catch ( e ) {
      console.log(`Error in ProjectsComponent addUpdateDraft ${draft.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
  }

  deleteDraft(draft: Draft) {
    try {
      let found = false;
      for ( const project of this.items ) {
        if ( project.drafts && !found ) {
          for ( let i = 0; i < project.drafts.length && !found; i++ ) {
            if ( project.drafts[i].getId() === draft.getId()) {
              found = true;
              project.drafts.splice(i, 1);
            }
          }
        }
      }
    } catch ( e ) {
      console.log(`Error in ProjectsComponent deleteDraft ${draft.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
  }

  addUpdateBook(book: Book) {
    try {
      let found = false;
      for ( const project of this.items ) {
        if ( project.books && !found ) {
          for ( let i = 0; i < project.books.length && !found; i++ ) {
            if ( project.books[i].getId() === book.getId()) {
              found = true;
              project.books.splice(i, 1, book);
              if ( project.primaryBook.getId() === book.getId()) {
                project.primaryBook = book;
              }
            }
          }
        }
      }
    } catch ( e ) {
      console.log(`Error in ProjectsComponent addUpdateBook ${book.getId()} ${e.message} ${this.startupService.selectedRole}:${this.startupService.selectedRoleId}`);
    }
  }

  contains(object: DataObject, array: DataObject[] ): boolean {
    for ( const o of array ) {
      if ( o.getId() === object.getId() ) {
        return true;
      }
    }
    return false;
  }

  computeFolderDecendantProjectCount() {
    this.folderDecendantProjectCount = {};

    for ( const project of this.items ) {
      if (this.folderDecendantProjectCount['']) {
        this.folderDecendantProjectCount['']++;
      } else {
        this.folderDecendantProjectCount[''] = 1;
      }

      const cumulativeFolders = Util.getCumulativeStrings(project.folder, '/');
      for ( const f of cumulativeFolders ) {
        if (this.folderDecendantProjectCount[f]) {
          this.folderDecendantProjectCount[f]++;
        } else {
          this.folderDecendantProjectCount[f] = 1;
        }
      }
    }
    // A Project arriving, changing folder, or disappearing can create or
    // clear a folder problem, and this method already runs on every one of
    // those. Keeping the two together means the banner cannot go stale.
    this.detectFolderProblems();
  }

  /**
   * Reconcile the organization's stored `projectFolders` tree with
   * the folder paths actually referenced by every currently-loaded
   * `Project`. For each `project.folder` value we compute the
   * cumulative ancestor paths (e.g. `"a/b/c"` → `"/a"`, `"/a/b"`,
   * `"/a/b/c"`) and add any that are missing from `projectFolders`
   * in parent-first order using `ProjectFolders.addFolder(parent,
   * leaf)`. If anything was added, persist a single
   * `organizationService.updateItem(...)` at the end.
   *
   * **Add-only invariant.** This method NEVER removes, renames, or
   * modifies any existing folder in the organization's tree. Even
   * if the loaded set of Projects doesn't reference a folder that
   * exists on the org (empty folder, orphan folder, folder for a
   * Project not currently in scope, etc.), the folder is left
   * untouched. Removing folders is an explicit operator action
   * handled by `DeleteProjectFolderComponent`, never by heal.
   *
   * ---------------------------------------------------------------
   * NO LONGER AUTOMATIC (2026-08-27)
   * ---------------------------------------------------------------
   * This used to run from `getData()` on every load. It does not any
   * more: `detectFolderProblems()` runs there instead, and this is
   * called only when an operator presses "Fix Folders" on the banner
   * that detection raises.
   *
   * The behaviour is identical; what changed is who decides. A silent
   * add-only reconcile on load meant every divergence was erased
   * before anyone could see it, so nobody could tell whether the two
   * stores drift once a year or once an hour — and the answer to that
   * is the whole basis for deciding whether this method should exist
   * at all. It also meant the repair happened before the operator
   * knew there had been a problem, which is the wrong order when the
   * "repair" is a guess: heal recreates the folder a Project claims
   * to be in, which is right when the tree lost an entry and wrong
   * when the Project's path was corrupted. The operator can tell
   * those apart from the folder name. This code cannot.
   *
   * ---------------------------------------------------------------
   * WHY IT STILL EXISTS AT ALL
   * ---------------------------------------------------------------
   * We proposed deleting it. The reasoning was that it was written to
   * paper over two specific defects — the non-atomic dual write
   * (PF-8) and the cross-organization rewrite (PF-12) — and that a
   * silent repair pass with no legitimate work left to do is a
   * liability, because the next genuine desynchronisation gets
   * quietly patched instead of reported.
   *
   * The server agreed with that reasoning and then supplied the fact
   * that invalidates the premise (inbox
   * `2026-08-27-folder-drift-sources-heal-not-vestigial.md`): **there
   * are two further drift sources, both live, and neither is a folder
   * endpoint.**
   *
   *   1. `PUT /project/{id}` writes `project.folder` verbatim — no
   *      field allow-list, no merge against the stored row.
   *   2. `PUT /organization/{id}` writes `organization.projectFolders`
   *      verbatim — it is a string column holding the whole tree.
   *
   * Our own models are what turn those from theoretical into real:
   * they copy every scalar off the response and echo it back on save.
   * So ANY component that loaded a Project or an Organization and
   * later saves it for an unrelated reason republishes whichever
   * folder value it happened to be holding. If a folder move happened
   * in between — another tab, another user — that unrelated save
   * silently reverts it. Last-write-wins on a stale read, with no
   * folder endpoint involved and nothing about it fixed by making the
   * folder endpoints atomic.
   *
   * So the repair stays available. It just is not applied behind the
   * operator's back any more.
   */
  healOrganizationFolderStructure(): void {
    if ( !this.organization || !this.projectFolders || !this.items ) {
      return;
    }

    // Enumerate existing folder paths for O(1) membership lookup.
    // These are the folders we will NEVER touch — heal is add-only.
    const existing = new Set<string>();
    try {
      const flat = this.projectFolders.getFlattened();
      if ( flat ) {
        for ( const f of flat ) {
          if ( typeof f === 'string' && f ) { existing.add(f); }
        }
      }
    } catch ( e ) {
      console.error('healOrganizationFolderStructure: failed to enumerate existing folders', e);
      return;
    }

    // Collect every cumulative folder path referenced by any loaded
    // Project. `ProjectFolders` stores paths with a leading '/'
    // (see `getFlattened()`), so we normalize the same way here.
    const referenced = new Set<string>();
    for ( const project of this.items ) {
      if ( !project ) { continue; }
      const raw = project.folder;
      if ( typeof raw !== 'string' ) { continue; }
      const trimmed = raw.trim();
      if ( !trimmed || trimmed === '/' ) { continue; }
      const segments = trimmed.split('/').map(s => s.trim()).filter(s => s.length > 0);
      let cumulative = '';
      for ( const seg of segments ) {
        cumulative = cumulative + '/' + seg;
        referenced.add(cumulative);
      }
    }

    // Diff → what's referenced by projects but missing from the
    // org's tree. We DO NOT compute the reverse diff (existing but
    // not referenced) — those folders stay put per the add-only
    // invariant documented above.
    const missing: string[] = [];
    referenced.forEach(p => { if ( !existing.has(p) ) { missing.push(p); } });
    if ( missing.length === 0 ) {
      return;
    }

    console.warn(`healOrganizationFolderStructure: repairing ${missing.length} missing folder(s) `
      + `on Organization ${this.organization.ID} at the operator's request: ${missing.join(', ')}`);

    // Parents must exist before children — sort shortest-first
    // (fewer '/' segments = higher in the tree).
    missing.sort((a, b) => a.split('/').length - b.split('/').length);

    let addedCount = 0;
    for ( const fullPath of missing ) {
      try {
        const lastSlash = fullPath.lastIndexOf('/');
        // lastSlash === 0 → parent is root '/', leaf is everything after.
        const parentPath = lastSlash <= 0 ? '/' : fullPath.substring(0, lastSlash);
        const leaf = fullPath.substring(lastSlash + 1);
        if ( !leaf ) { continue; }
        this.projectFolders.addFolder(parentPath, leaf);
        addedCount++;
      } catch ( e ) {
        console.error(`healOrganizationFolderStructure: could not add folder ${fullPath}`, e);
      }
    }

    if ( addedCount === 0 ) {
      return;
    }

    // Persist the reconciled tree.
    try {
      this.organization.setProjectFolders(this.projectFolders);
      this.organizationService.updateItem(this.organization).subscribe(
        () => {
          console.log(`healOrganizationFolderStructure: added ${addedCount} missing folder(s) to Organization ${this.organization.ID}`);
          // The banner is driven off `folderProblems`, so it has to be
          // recomputed or the operator sees the warning they just fixed.
          this.detectFolderProblems();
          try { this.changeDetectorRefStored.detectChanges(); } catch ( e ) { /* view may be gone */ }
        },
        err => {
          console.error('healOrganizationFolderStructure: updateItem failed', err);
          // The tree was mutated in memory before the PUT. A failed persist
          // leaves this session showing folders the server does not have, and
          // the next reload would silently drop them — so put the in-memory
          // copy back and leave the banner up.
          this.projectFolders = this.organization.getProjectFolders();
          this.detectFolderProblems();
          this.serverErrorToastService.reportError('healOrganizationFolderStructure', err,
            LanguagePipe.get('The folders could not be repaired.'));
        }
      );
    } catch ( e ) {
      console.error('healOrganizationFolderStructure: persist step failed', e);
    }
  }

  /**
   * Operator-invoked repair from the folder-problem banner: add every folder
   * a Project references but the tree is missing, then persist once.
   *
   * Deliberately the same code path heal always used, so there is one repair
   * with one set of guarantees rather than a second, subtly different one
   * written for the button.
   */
  fixFolderProblems(): void {
    if ( !this.hasFolderProblems ) {
      return;
    }
    this.healOrganizationFolderStructure();
  }

  getData(reset: boolean = true): void {
    this.goose++;
    this.startupService.touchLastUserActivity();
    this.items = [];

    this.organizationService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      if ( !Util.deepEqual(this.projectFolders, o.getProjectFolders() ) ) {
        this.projectFolders = o.getProjectFolders();
        // The tree just changed under us — another tab, another operator, or
        // our own repair. Whether a Project is orphaned is a question about
        // BOTH stores, so a change to either one can create or clear a
        // problem.
        this.detectFolderProblems();
      }
    });

    this.projectService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateProject(o);
      this.computeFolderDecendantProjectCount();
      this.tryApplyPendingSelection();
    });
    this.projectService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateProject(o);
      this.computeFolderDecendantProjectCount();
      this.tryApplyPendingSelection();
    });
    this.projectService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.deleteProject(o);
      this.computeFolderDecendantProjectCount();
    });
    this.projectService.objects.forEach( o => {
      this.addUpdateProject(o);
    });
    this.computeFolderDecendantProjectCount();
    // All currently-loaded Projects are now in `this.items`. Compare the
    // folders they claim to be in against the folders the Organization says
    // exist, and REPORT the difference — this used to repair it silently, see
    // `healOrganizationFolderStructure`. Nothing is written here.
    this.detectFolderProblems();

    this.draftService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateDraft(o);
      // A pending "open the new Draft" intent cannot complete until
      // the Draft itself has arrived and been linked onto its
      // Project — this is where that becomes true.
      this.tryApplyPendingSelection();
    });
    this.draftService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateDraft(o);
      this.tryApplyPendingSelection();
    });
    this.draftService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.deleteDraft(o);
    });

    this.bookService.getAddNotifier(false).pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateBook(o);
    });
    this.bookService.getUpdateNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
      this.addUpdateBook(o);
    });
    // this.bookService.getDeleteNotifier().pipe(takeUntil(this.onDestroySubject)).subscribe( o => {
    //   this.deleteBook(o);
    // });
  }

  editingDetailsChanged(newEditingDetails: string) {
    this.editingDetails = newEditingDetails;
  }
  /**
   * Leave the detail pane for the grid.
   *
   * The pinned Draft / Book has to be dropped here too, or the next Project
   * opened would inherit it.
   */
  closeDetails() {
    this.editingDetails = '';
    this.selectedItem = undefined;
    this.selectedDraft = undefined;
    this.selectedBook = undefined;
  }
  displayCoverChanged(newDisplayCover: boolean) {
    this.displayCover = newDisplayCover;
  }
  displaySettingsChanged(newDisplaySettings: boolean) {
    this.displaySettings = newDisplaySettings;
  }
  onDraftFileSelected(event: Event) {
    if (this.draftComponent) {
      this.draftComponent.onFileSelected(event);
    }
  }
  /**
   * A thumbnail asked to open a Project (double click, or one of
   * its own menu items).
   *
   * The selection is dropped here rather than at each call site
   * because this is the single funnel every open path goes
   * through, and a selection bar left standing over a detail pane
   * offers to move Projects the operator can no longer see.
   */
  /**
   * Accepts a Project, Draft or Book.
   *
   * The thumbnails emit whichever object was actually opened; `selectedItem`
   * still ends up holding the Project, because the grid compares against it
   * (`[class.selected]="item === selectedItem"`) and the toolbar reads its
   * name. A Draft or Book additionally pins the detail pane to that exact
   * object - see `selectedDraft` / `selectedBook`.
   */
  selectedItemChanged(item: Project | Draft | Book) {
    this.clearSelection();
    // A sub-item selection belongs to the Project it was made in, so it is
    // cleared on every selection change and only re-set just below.
    this.selectedDraft = undefined;
    this.selectedBook = undefined;
    let project: Project;
    if ( item instanceof Draft ) {
      this.selectedDraft = item;
      project = item.project;
    } else if ( item instanceof Book ) {
      this.selectedBook = item;
      project = item.project;
    } else {
      project = item as Project;
    }
    this.selectedItem = project;
    if ( this.selectedItem ) {
      this.mainSideNav.close();
    } else {
      this.mainSideNav.open();
    }
  }
  changeFolder(newFolder: string) {
    // A selection is scoped to the folder it was made in. Carrying
    // it across a navigation leaves the bar counting tiles that are
    // no longer drawn; 'selectedProjectList' would prune them, but
    // silently, so the count would drop for no visible reason.
    this.clearSelection();
    this.selectedFolder = newFolder;
  }

  createNewFolder(selectedFolder: string) {
    console.log(`Create New Folder ${selectedFolder}`);

    const dialogConfig = modernDialogConfig({ width: '520px' });
    dialogConfig.data = { organization: this.organization, selectedFolder: selectedFolder };
    const dialogRef = this.dialog.open(CreateProjectFolderComponent, dialogConfig);
  }

  moveFolder(selectedFolder: string) {
    console.log(`Move Folder ${selectedFolder}`);

    const dialogConfig = modernDialogConfig({ width: '640px' });
    dialogConfig.data = { organization: this.organization, selectedFolder: selectedFolder };
    const dialogRef = this.dialog.open(MoveProjectFolderComponent, dialogConfig);
  }

  renameFolder(selectedFolder: string) {
    console.log(`Rename Folder ${selectedFolder}`);

    // Change name in the organization
    // Change the values in the objects that point to the folder
    const dialogConfig = modernDialogConfig({ width: '520px' });
    dialogConfig.data = { organization: this.organization, selectedFolder: selectedFolder };
    const dialogRef = this.dialog.open(RenameProjectFolderComponent, dialogConfig);
  }

  canDeleteFolder(selectedFolder: string): boolean {
    // console.log(`Can Delete Folder ${selectedFolder} ${!this.folderDecendantProjectCount[selectedFolder] || this.folderDecendantProjectCount[selectedFolder] === 0}`);
    return !this.folderDecendantProjectCount[selectedFolder] || this.folderDecendantProjectCount[selectedFolder] === 0;
  }
  deleteFolder(selectedFolder: string) {
    console.log(`Delete Folder ${selectedFolder}`);

    // Delete the folder if there are no projects in the folder or any sub-folders
    const dialogConfig = modernDialogConfig({ width: '520px' }, 'compact');
    dialogConfig.data = { organization: this.organization, selectedFolder: selectedFolder };
    const dialogRef = this.dialog.open(DeleteProjectFolderComponent, dialogConfig);
  }
}
